import { useCallback, useEffect, useRef, useState } from "react";

export interface UseCopyToClipboardResult {
  /**
   * Copies the given value to the clipboard. Resolves to `true` on success and `false` on
   * failure (it never throws), so callers can branch their feedback on the result.
   */
  copy: (value: string) => Promise<boolean>;
  /**
   * `true` for `resetDelay` ms after a successful copy, then automatically flips back to `false`.
   * Useful for swapping the copy affordance to a "copied" state.
   */
  isCopied: boolean;
}

/**
 * Writes the legacy way for browsers / contexts where the async Clipboard API is unavailable
 * (non-secure origins, older browsers). Returns `true` when the copy succeeded.
 */
const _copyWithExecCommand = (value: string): boolean => {
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

/**
 * Reusable clipboard hook with no UI concerns. It performs the copy and tracks a transient
 * `isCopied` flag; consumers decide how to confirm to the user (toast, icon swap, etc.).
 *
 * @param resetDelay how long `isCopied` stays `true` after a successful copy, in milliseconds
 */
export const useCopyToClipboard = (resetDelay = 2000): UseCopyToClipboardResult => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy = useCallback(
    async (value: string): Promise<boolean> => {
      let success = false;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          success = true;
        } else {
          success = _copyWithExecCommand(value);
        }
      } catch {
        success = _copyWithExecCommand(value);
      }

      if (success) {
        setIsCopied(true);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setIsCopied(false), resetDelay);
      }

      return success;
    },
    [resetDelay]
  );

  return { copy, isCopied };
};
